import React, { useContext, useMemo } from "react";
import { MainContext } from "../../context/MainContext";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import InputOutlinedIcon from "@mui/icons-material/InputOutlined";
import OutputOutlinedIcon from "@mui/icons-material/OutputOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import BluetoothOutlinedIcon from "@mui/icons-material/BluetoothOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";

import * as Constants from "../api/constants";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FIELDS = [
  { id: 0, label: "Command Echo", icon: <SyncOutlinedIcon /> },

  { id: 1, label: "GPIO Input 1 - Status (SG1 input, S200)", icon: <InputOutlinedIcon /> },
  { id: 2, label: "GPIO Input 2 - Status (SG2 input, S201 or S202)", icon: <InputOutlinedIcon /> },
  { id: 3, label: "GPIO Output 1 - Status (Spa heating output, S203)", icon: <OutputOutlinedIcon /> },

  { id: 4, label: "Client 1 - Connected", icon: <LinkOutlinedIcon /> },
  { id: 5, label: "Client 1 - IP Address", icon: <LanguageOutlinedIcon /> },
  { id: 6, label: "Client 1 - Mac Address", icon: <BluetoothOutlinedIcon /> },
  { id: 7, label: "Client 1 - Wi-Fi RSSI", icon: <WifiOutlinedIcon /> },
  { id: 8, label: "Client 1 - Type", icon: <LinkOutlinedIcon /> },
  { id: 9, label: "Client 1 - Heat Signal", icon: <LocalFireDepartmentOutlinedIcon /> },

  { id: 10, label: "Client 2 - Connected", icon: <LinkOutlinedIcon /> },
  { id: 11, label: "Client 2 - IP Address", icon: <LanguageOutlinedIcon /> },
  { id: 12, label: "Client 2 - Mac Address", icon: <BluetoothOutlinedIcon /> },
  { id: 13, label: "Client 2 - Wi-Fi RSSI", icon: <WifiOutlinedIcon /> },
  { id: 14, label: "Client 2 - Type", icon: <LinkOutlinedIcon /> },
  { id: 15, label: "Client 2 - Heat Signal", icon: <LocalFireDepartmentOutlinedIcon /> },

  { id: 16, label: "Client 3 - Connected", icon: <LinkOutlinedIcon /> },
  { id: 17, label: "Client 3 - IP Address", icon: <LanguageOutlinedIcon /> },
  { id: 18, label: "Client 3 - Mac Address", icon: <BluetoothOutlinedIcon /> },
  { id: 19, label: "Client 3 - Wi-Fi RSSI", icon: <WifiOutlinedIcon /> },
  { id: 20, label: "Client 3 - Type", icon: <LinkOutlinedIcon /> },
  { id: 21, label: "Client 3 - Heat Signal", icon: <LocalFireDepartmentOutlinedIcon /> },
];

function valueOrNA(arr, i) {
  const v = arr?.[i];
  return v === undefined || v === null || v === "" ? "N/A" : v;
}

export default function SsigSnSsigR2() {
  const state = useContext(MainContext).state;
  const deviceId = state.selectedDevice;

  const sn = (state.snSsigR2 ?? "").split(",");

  const sendCommand = (command, value) => {
    fetch(Constants.baseURL + Constants.apiSendCommand, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ DeviceId: deviceId, Command: command, Value: value }),
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          toast.success("Command sent successfully. Refreshing...", {
            position: "top-right",
            autoClose: 10000,
          });

          setTimeout(() => state.refreshDeviceRF(deviceId), 6000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 12000);
        } else {
          toast.error("Send failed. Please try again", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      })
      .catch((err) => console.log("request failed", err));
  };

  const onUpdate = () => sendCommand("SN-SSIG-R2", "");

  const cards = useMemo(() => {
    return FIELDS.map((f) => ({
      ...f,
      value: valueOrNA(sn, f.id),
    }));
  }, [sn]);

  return (
    <>
      <ToastContainer />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography component="h2" variant="h6" color="white" sx={{ fontWeight: 600 }}>
          SN-SSIG-R2 Data
        </Typography>

        <IconButton
          onClick={onUpdate}
          sx={{
            color: "white",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "50%",
            width: 40,
            height: 40,
            "&:hover": {
              borderColor: "white",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {cards.map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={6} key={item.id}>
            <Paper
              sx={{
                p: 1.4,
                minHeight: 86,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                transition: "0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {/* Icon bubble */}
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.75)",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>

              {/* Label + Value */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                    lineHeight: 1.2,
                  }}
                >
                  {item.label}
                </Typography>

                <Box sx={{ mt: 0.8, display: "flex", alignItems: "flex-end" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.65)",
                      fontWeight: 700,
                      fontSize: 12,
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
